import { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import FormInput from '../components/ui/FormInput';

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const { theme } = useTheme();

  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (submitting) return;

    setError('');
    setSubmitting(true);

    try {
      const response = await fetch('/api/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });

      const data = await response.json();
      if (data.error) {
        setError(data.error);
      } else {
        setSubmitted(true);
      }
    } catch {
      setError('Er ging iets mis. Probeer het later opnieuw.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md animate-enter">
        {/* Logo */}
        <div className="text-center mb-8">
          {theme.logo ? (
            <img src={theme.logo} alt={theme.name} className="h-12 mx-auto mb-2" />
          ) : (
            <h1 className="text-4xl font-bold tracking-tight">
              <span className="text-primary">{theme.name.toLowerCase()}</span>
            </h1>
          )}
        </div>

        <div className="glass-card p-8">
          {submitted ? (
            <div className="text-center py-4">
              <CheckCircle className="w-12 h-12 text-success mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-foreground mb-2">E-mail verzonden</h2>
              <p className="text-muted-foreground mb-6">
                Als het e-mailadres bij ons bekend is, ontvang je een link om je wachtwoord opnieuw in te stellen.
              </p>
              <button
                onClick={() => navigate('/')}
                className="btn-primary px-6 py-3 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold rounded-xl transition-colors"
              >
                Terug naar inloggen
              </button>
            </div>
          ) : (
            <>
              <h2 className="text-xl font-semibold text-foreground mb-2 text-center">
                Wachtwoord vergeten?
              </h2>
              <p className="text-muted-foreground text-sm text-center mb-6">
                Vul je e-mailadres in en we sturen je een link om je wachtwoord opnieuw in te stellen.
              </p>

              {error && (
                <div className="mb-4 p-3 bg-destructive/10 border border-destructive/30 rounded-lg flex items-center gap-2 text-destructive text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <FormInput
                  icon={Mail}
                  label="E-mailadres"
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="name@company.com"
                  required
                  autoFocus
                />

                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary w-full py-3 bg-primary hover:bg-primary/90 disabled:bg-primary/50 text-primary-foreground font-semibold rounded-xl transition-colors flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Verzenden...
                    </>
                  ) : (
                    'Reset link versturen'
                  )}
                </button>
              </form>

              <div className="mt-4 text-center">
                <button
                  onClick={() => navigate('/')}
                  className="text-sm text-muted-foreground hover:text-primary transition-colors"
                >
                  Terug naar inloggen
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
